import { Link } from "react-router-dom";
import { Award, BookOpen, BrainCircuit, CalendarDays, Clock, ShieldCheck, Sparkles, Target, TrendingUp, Zap } from "lucide-react";
import { ContextualNav } from "@/components/ContextualNav";
import { MasteryBar, formatDuration } from "@/components/States";

const QUICK_ACTIONS = [
  { to: "/p/calendario", icon: "calendar", label: "Organizar exámenes", description: "Materias, fechas y notas reales." },
  { to: "/p/material", icon: "material", label: "Preparar material", description: "Sube apuntes y crea ejercicios." },
  { to: "/p/asistente", icon: "assistant", label: "Consultar al asistente", description: "Recibe orientación basada en datos." },
];

export function ParentDashboardContent({ data }) {
  const { totals, mastery, weaknesses, strengths, recent_sessions: recent, upcoming_exams: exams, plan, daily } = data;
  const hasProgress = totals.questions_answered > 0 || totals.sessions > 0;

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      <DashboardHero student={data.student} totals={totals} />
      <DashboardActions hasProgress={hasProgress} />
      <DashboardMetrics totals={totals} />
      <CycleOverview cycles={data.exam_cycles || []} xpBalance={data.xp_balance || 0} />
      {hasProgress ? (
        <ProgressDashboard
          daily={daily}
          plan={plan}
          mastery={mastery}
          strengths={strengths}
          weaknesses={weaknesses}
          exams={exams}
          recent={recent}
        />
      ) : (
        <GettingStarted exams={exams} />
      )}
    </div>
  );
}

function CycleOverview({ cycles, xpBalance }) {
  const cycle = cycles[0];
  return (
    <section className="play-card overflow-hidden bg-slate-900 p-5 text-white sm:p-7" data-testid="parent-xp-cycle">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><span className="play-icon h-14 w-14 bg-fuchsia-500"><Zap className="h-8 w-8 fill-current" /></span><div><p className="text-xs font-black uppercase tracking-[.18em] text-fuchsia-300">Progreso global</p><p className="font-display text-3xl font-black">{xpBalance.toLocaleString()} XP</p></div></div>
        <p className="max-w-md text-sm font-semibold text-slate-300">El resultado y el esfuerzo se muestran por separado para reconocer cómo aprendió, no solamente la nota.</p>
      </div>
      {cycle ? (
        <div className="mt-6">
          <div className="flex flex-wrap items-end justify-between gap-2"><div><h2 className="font-display text-2xl font-black">{cycle.subject} · {cycle.title}</h2><p className="text-sm text-slate-400">Ciclo más reciente</p></div><p className="font-display text-3xl font-black text-lime-300">+{cycle.totalXP} XP</p></div>
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <ParentXp label="Esfuerzo" value={cycle.effortXP} tone="bg-cyan-400 text-cyan-950" />
            <ParentXp label="Mejora" value={cycle.improvementXP} tone="bg-lime-300 text-lime-950" />
            <ParentXp label="Nota" value={cycle.gradeXP} tone="bg-amber-300 text-amber-950" />
            <ParentXp label="Corrección" value={cycle.correctionXP} tone="bg-pink-400 text-pink-950" />
            <ParentXp label="Bonus" value={cycle.bonusXP} tone="bg-violet-400 text-violet-950" />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <CycleMetric icon={Target} label="Exam Ready previo" value={`${cycle.examReady || 0}%`} />
            <CycleMetric icon={TrendingUp} label="Evolución" value={cycle.growth == null ? "Primer baseline" : `${cycle.growth > 0 ? "+" : ""}${cycle.growth}%`} />
            <CycleMetric icon={ShieldCheck} label="Errores corregidos" value={`${cycle.errorsCorrected}/${cycle.errorsTotal}`} />
          </div>
        </div>
      ) : <p className="mt-5 text-sm font-semibold text-slate-400">Los ciclos aparecerán al vincular material y actividad a un examen.</p>}
    </section>
  );
}

function ParentXp({ label, value, tone }) {
  return <div className={`rounded-2xl p-3 ${tone}`}><p className="text-xs font-black uppercase tracking-wide opacity-70">{label}</p><p className="font-display text-2xl font-black">+{value}</p></div>;
}

function CycleMetric({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-3"><Icon className="h-5 w-5 text-cyan-300" /><div><p className="text-xs font-bold text-slate-400">{label}</p><p className="font-display text-lg font-extrabold">{value}</p></div></div>;
}

function DashboardHero({ student, totals }) {
  return (
    <div className="play-card relative overflow-hidden bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-500 p-6 text-white sm:p-8">
      <span className="play-doodle -right-10 -top-14 h-52 w-52 border-[30px] border-white/10" />
      <span className="play-icon h-14 w-14 bg-amber-400 text-slate-900">
        <Sparkles className="h-8 w-8 animate-wiggle" />
      </span>
      <p className="mt-4 text-xs font-extrabold uppercase tracking-[.16em] text-violet-200">Panel familiar</p>
      <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
        La aventura de {student.first_name}
      </h1>
      <p className="mt-2 max-w-2xl text-sm font-semibold text-violet-100">
        {totals.questions_answered > 0
          ? "Datos reales de sus sesiones de estudio."
          : "Todavia no hay sesiones registradas. Sube apuntes y deja que practique."}
      </p>
    </div>
  );
}

function DashboardActions({ hasProgress }) {
  return (
    <ContextualNav
      eyebrow={hasProgress ? "Acciones rápidas" : "Primeros pasos"}
      title={hasProgress ? "Acompaña su siguiente logro" : "Pon en marcha su aventura"}
      description={
        hasProgress
          ? "Salta directamente a la tarea que quieres gestionar."
          : "Prepara el calendario y el material; después el progreso aparecerá aquí automáticamente."
      }
      items={QUICK_ACTIONS}
      testId="dashboard-quick-actions"
    />
  );
}

function DashboardMetrics({ totals }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Metric icon={Clock} label="Tiempo estudiado" value={formatDuration(totals.study_seconds)} testId="metric-time" tone="bg-cyan-300" iconTone="bg-cyan-600" tilt="-rotate-[.5deg]" />
      <Metric icon={BookOpen} label="Sesiones" value={totals.sessions} testId="metric-sessions" tone="bg-amber-300" iconTone="bg-orange-500" tilt="rotate-[.5deg]" />
      <Metric icon={BrainCircuit} label="Preguntas" value={totals.questions_answered} testId="metric-questions" tone="bg-pink-300" iconTone="bg-pink-600" tilt="rotate-[.4deg]" />
      <Metric
        icon={TrendingUp}
        label="Aciertos"
        value={totals.accuracy === null ? "—" : `${Math.round(totals.accuracy * 100)}%`}
        hint={`${totals.correct} correctas`}
        testId="metric-accuracy"
        tone="bg-lime-300"
        iconTone="bg-emerald-700"
        tilt="-rotate-[.4deg]"
      />
    </div>
  );
}

function Metric({ icon: Icon, label, value, hint, testId, tone, iconTone, tilt = "" }) {
  return (
    <div className={`play-card ${tone} ${tilt} min-h-[170px] p-4 sm:p-5`} data-testid={testId}>
      <span className={`play-icon h-14 w-14 sm:h-16 sm:w-16 ${iconTone}`}>
        <Icon className="h-8 w-8 sm:h-9 sm:w-9" />
      </span>
      <p className="mt-4 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">{value}</p>
      <p className="text-xs font-extrabold text-slate-800 sm:text-sm">{label}</p>
      {hint && <p className="mt-1 text-xs font-semibold text-slate-700">{hint}</p>}
    </div>
  );
}

function ProgressDashboard({ daily, plan, mastery, strengths, weaknesses, exams, recent }) {
  return (
    <>
      <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <EvolutionSection daily={daily} />
        <PlanSection plan={plan} />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <MasterySection mastery={mastery} />
        <div className="space-y-5">
          <StrengthsSection strengths={strengths} weaknesses={weaknesses} />
          <ExamsSection exams={exams} />
        </div>
      </div>
      <RecentSessionsSection recent={recent} />
    </>
  );
}

function EvolutionSection({ daily }) {
  const maxDaily = Math.max(1, ...daily.map((day) => day.study_seconds));
  return (
    <section className="play-card bg-white p-5" data-testid="dashboard-evolution">
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Evolución · 14 días</h2>
      {daily.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">Sin actividad todavia.</p>
      ) : (
        <div className="mt-6 flex h-40 items-end gap-2">
          {daily.map((day) => (
            <div key={day.date} className="flex flex-1 flex-col items-center gap-2" title={`${day.date}: ${day.answered} preguntas`}>
              <div
                className="w-full rounded-t-xl bg-gradient-to-t from-violet-600 to-cyan-400 transition-[height] duration-500"
                style={{ height: `${Math.max(6, (day.study_seconds / maxDaily) * 130)}px` }}
              />
              <span className="text-[10px] text-slate-400">{day.date.slice(8)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PlanSection({ plan }) {
  return (
    <section className="play-card bg-amber-100 p-5" data-testid="dashboard-plan">
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Misiones de hoy</h2>
      {plan.has_data ? (
        <ul className="mt-4 space-y-3">
          {plan.items.map((item) => (
            <li key={item.study_pack_id} className="flex items-center justify-between gap-3" data-testid="plan-item">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{item.subject}</p>
                <p className="truncate text-xs text-slate-500">{item.title}</p>
              </div>
              <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                {item.minutes} min
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-slate-500">{plan.message}</p>
      )}
    </section>
  );
}

function MasterySection({ mastery }) {
  return (
    <section className="play-card bg-white p-5" data-testid="dashboard-mastery">
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Poder por tema</h2>
      {mastery.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Aun no hay evidencia suficiente por tema.</p>
      ) : (
        <ul className="mt-4 space-y-4">
          {mastery.slice(0, 8).map((item) => (
            <li key={item.topic_id} data-testid="mastery-row">
              <div className="flex items-baseline justify-between gap-3">
                <p className="truncate text-sm font-semibold text-slate-800">{item.topic}</p>
                <span className="shrink-0 text-xs text-slate-400">{item.subject}</span>
              </div>
              <div className="mt-1.5"><MasteryBar value={item.mastery} /></div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StrengthsSection({ strengths, weaknesses }) {
  return (
    <section className="play-card bg-lime-100 p-5" data-testid="dashboard-strengths-weaknesses">
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Superpoderes y retos</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TopicList label="Fortalezas" items={strengths} tone="text-emerald-600" />
        <TopicList label="A reforzar" items={weaknesses} tone="text-rose-600" />
      </div>
    </section>
  );
}

function TopicList({ label, items, tone }) {
  return (
    <div>
      <p className={`text-xs font-bold uppercase tracking-wide ${tone}`}>{label}</p>
      {items.length ? (
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          {items.map((item) => <li key={item.topic_id}>{item.topic}</li>)}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-400">Sin datos suficientes</p>
      )}
    </div>
  );
}

function ExamsSection({ exams }) {
  return (
    <section className="play-card bg-cyan-50 p-5" data-testid="dashboard-exams">
      <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900">
        <span className="play-icon h-11 w-11 bg-cyan-500"><CalendarDays className="h-6 w-6" /></span> Próximos exámenes
      </h2>
      {exams.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No hay examenes registrados.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {exams.slice(0, 4).map((exam) => <ExamRow key={exam.id} exam={exam} />)}
        </ul>
      )}
    </section>
  );
}

function ExamRow({ exam }) {
  return (
    <li className="flex items-center justify-between gap-3 text-sm" data-testid="exam-row">
      <span className="min-w-0">
        <span className="block truncate font-semibold text-slate-800">{exam.title}</span>
        <span className="text-xs text-slate-500">{exam.subject}</span>
      </span>
      <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${exam.days_left <= 3 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
        {exam.days_left < 0 ? "pasado" : `${exam.days_left} d`}
      </span>
    </li>
  );
}

function RecentSessionsSection({ recent }) {
  return (
    <section className="play-card bg-white p-5" data-testid="dashboard-recent">
      <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900">
        <span className="play-icon h-11 w-11 bg-pink-500"><Award className="h-6 w-6" /></span> Aventuras recientes
      </h2>
      {recent.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Sin sesiones todavia.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="pb-2">Fecha</th><th className="pb-2">Materia</th><th className="pb-2">Preguntas</th>
                <th className="pb-2">Aciertos</th><th className="pb-2">Tiempo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((session) => (
                <tr key={session.id} data-testid="recent-session-row">
                  <td className="py-2 text-slate-500">{new Date(session.started_at).toLocaleDateString()}</td>
                  <td className="py-2 font-semibold text-slate-800">{session.subject}</td>
                  <td className="py-2">{session.answered}</td>
                  <td className="py-2">{session.answered ? `${Math.round((session.correct / session.answered) * 100)}%` : "—"}</td>
                  <td className="py-2 text-slate-500">{formatDuration(session.duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function GettingStarted({ exams }) {
  return (
    <section className="play-card bg-cyan-50 p-5 sm:p-6" data-testid="dashboard-get-started">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[.16em] text-cyan-700">Lo importante ahora</p>
          <h2 className="mt-1 font-display text-2xl font-extrabold text-slate-900">Próximos exámenes</h2>
          <p className="mt-1 text-sm text-slate-500">Mantén las fechas visibles mientras preparas el primer material.</p>
        </div>
        <Link to="/p/calendario" className="rounded-xl bg-cyan-700 px-4 py-2 text-sm font-extrabold text-white hover:bg-cyan-800">
          Gestionar calendario
        </Link>
      </div>
      {exams.length === 0 ? (
        <p className="mt-5 rounded-2xl bg-white p-4 text-sm font-semibold text-slate-500">
          Aún no hay exámenes. Registra uno para convertirlo en un objetivo de estudio.
        </p>
      ) : (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {exams.slice(0, 4).map((exam) => (
            <li key={exam.id} className="rounded-2xl bg-white p-4" data-testid="exam-row">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block truncate font-display font-extrabold text-slate-900">{exam.title}</span>
                  <span className="text-xs font-semibold text-slate-500">{exam.subject}</span>
                </span>
                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${exam.days_left <= 3 ? "bg-rose-100 text-rose-700" : "bg-cyan-100 text-cyan-700"}`}>
                  {exam.days_left < 0 ? "pasado" : `${exam.days_left} d`}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
